import { api } from "../../lib";

// Stain type enum
export type StainType = "H&E" | "IHC" | "ISH" | "Special" | "Other";

// Stain Provider interface
export interface StainProvider {
  id: string;
  name: string;
  address?: string;
  email?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  fax?: string;
  contactName?: string;
  averageTurnaroundDays?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Stain Provider Capability interface
export interface StainProviderCapability {
  id: string;
  stainProviderId: string;
  stainId: string;
  cost?: number;
  turnaroundDays?: number;
  isPreferred: boolean;
  isActive: boolean;
  createdAt: string;
  stainProvider?: StainProvider;
  stain?: Stain;
}

export interface Stain {
  id: string;
  code: string;
  description: string;
  leicaMarker?: string | null;
  biocareMarker?: string | null;
  cptId: string | null;
  stainType: StainType;
  isAvailableInHouse: boolean;
  defaultProviderId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  cptCode?: { code: string; description: string } | null;
  defaultProvider?: StainProvider | null;
  markerVerification?: {
    biocare?: StainMarkerVerificationSummary | null;
    leica?: StainMarkerVerificationSummary | null;
  } | null;
}

export interface StainMarkerVerificationSummary {
  status: StainMarkerVerificationStatus;
  testedAt?: string | null;
  markerValueSent?: string | null;
  ackCode?: string | null;
  rejectionReason?: string | null;
  errorMessage?: string | null;
  jobId?: string | null;
}

export interface CreateStainInput {
  code: string;
  description: string;
  leicaMarker?: string | null;
  biocareMarker?: string | null;
  stainType?: StainType;
  cptId?: string | null;
  isAvailableInHouse?: boolean;
  defaultProviderId?: string | null;
  isActive?: boolean;
}

export interface UpdateStainInput extends Partial<CreateStainInput> {
  id: string;
}

// Stain filters for the filter modal
export interface StainFilters {
  stainTypes?: StainType[];
  availability?: "all" | "inHouse" | "external";
  status?: "all" | "active" | "inactive";
  cptCodeIds?: string[]; // Selected CPT code IDs to filter by
  showWithoutCpt?: boolean; // Include stains without CPT codes
}

// Search params for API
export interface StainSearchParams {
  search?: string;
  stainTypes?: StainType[];
  availability?: "all" | "inHouse" | "external";
  status?: "all" | "active" | "inactive";
  cptCodeIds?: string[];
  showWithoutCpt?: boolean;
}

export const stainsService = {
  getAll: async (params?: StainSearchParams) => {
    const searchParams = new URLSearchParams();

    if (params?.search) {
      searchParams.append("search", params.search);
    }
    if (params?.stainTypes && params.stainTypes.length > 0) {
      searchParams.append("stainTypes", params.stainTypes.join(","));
    }
    if (params?.availability && params.availability !== "all") {
      searchParams.append("availability", params.availability);
    }
    if (params?.status && params.status !== "all") {
      searchParams.append("status", params.status);
    }
    if (params?.cptCodeIds && params.cptCodeIds.length > 0) {
      searchParams.append("cptCodeIds", params.cptCodeIds.join(","));
    }
    if (params?.showWithoutCpt) {
      searchParams.append("showWithoutCpt", "true");
    }

    const queryString = searchParams.toString();
    const url = queryString ? `/admin/stains?${queryString}` : "/admin/stains";

    return api.get<Stain[]>(url);
  },

  getById: async (id: string) => {
    return api.get<Stain>(`/admin/stains/${id}`);
  },

  create: async (input: CreateStainInput) => {
    return api.post<Stain>("/admin/stains", input);
  },

  update: async (input: UpdateStainInput) => {
    const { id, ...updates } = input;
    return api.put<Stain>(`/admin/stains/${id}`, updates);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/stains/${id}`);
  },

  toggleActive: async (id: string, isActive: boolean) => {
    return api.put<Stain>(`/admin/stains/${id}`, { isActive });
  },

  // Provider capabilities for a specific stain
  getProviderCapabilities: async (stainId: string) => {
    return api.get<StainProviderCapability[]>(
      `/admin/stains/${stainId}/provider-capabilities`
    );
  },

  addProviderCapability: async (
    stainId: string,
    data: {
      providerId: string;
      cost?: number;
      turnaroundDays?: number;
      isPreferred?: boolean;
    }
  ) => {
    return api.post<StainProviderCapability>(
      `/admin/stains/${stainId}/provider-capabilities`,
      data
    );
  },

  updateProviderCapability: async (
    capabilityId: string,
    data: {
      cost?: number;
      turnaroundDays?: number;
      isPreferred?: boolean;
      isActive?: boolean;
    }
  ) => {
    return api.put<StainProviderCapability>(
      `/admin/stain-provider-capabilities/${capabilityId}`,
      data
    );
  },

  deleteProviderCapability: async (capabilityId: string) => {
    await api.delete<void>(
      `/admin/stain-provider-capabilities/${capabilityId}`
    );
  },

  // Marker Verification
  startMarkerVerification: async (input: StartMarkerVerificationInput) => {
    return api.post<MarkerVerificationJobResponse>(
      "/admin/stains/marker-verification/jobs",
      input
    );
  },

  getMarkerVerificationJob: async (jobId: string) => {
    return api.get<MarkerVerificationJobStatus>(
      `/admin/stains/marker-verification/jobs/${jobId}`
    );
  },

  pauseMarkerVerificationJob: async (jobId: string) => {
    return api.post<void>(
      `/admin/stains/marker-verification/jobs/${jobId}/pause`
    );
  },

  resumeMarkerVerificationJob: async (jobId: string) => {
    return api.post<void>(
      `/admin/stains/marker-verification/jobs/${jobId}/resume`
    );
  },

  getMarkerVerificationResults: async (params?: {
    provider?: StainMarkerVerificationProvider;
    status?: StainMarkerVerificationStatus;
    stainId?: string;
    jobId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.append(key, String(value));
      });
    }
    const queryString = searchParams.toString();
    const url = queryString
      ? `/admin/stains/marker-verification/results?${queryString}`
      : "/admin/stains/marker-verification/results";

    return api.get<{
      data: StainMarkerVerificationResult[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>(url);
  },
};

export type StainMarkerVerificationProvider = "biocare" | "leica";
export type StainMarkerVerificationStatus =
  | "pending"
  | "sent"
  | "accepted"
  | "rejected"
  | "failed"
  | "timed_out"
  | "dry_run";

export interface StainMarkerVerificationResult {
  id: string;
  stainId: string;
  provider: StainMarkerVerificationProvider;
  protocol: "biocare" | "bond";
  status: StainMarkerVerificationStatus;
  markerValueSent?: string | null;
  messageControlId?: string | null;
  bridgeCommandId?: string | null;
  jobId?: string | null;
  workstationId?: string | null;
  targetKey?: string | null;
  ackCode?: string | null;
  orderControl?: string | null;
  rejectionReason?: string | null;
  sentMessage?: string | null;
  rawResponse?: string | null;
  errorMessage?: string | null;
  testedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  stain?: Stain;
}

export interface StartMarkerVerificationInput {
  providers: Array<StainMarkerVerificationProvider>;
  scope: "all" | "untested" | "failed_or_untested";
  dryRun?: boolean;
  confirmedLiveRun?: boolean;
  stainIds?: string[];
}

export interface MarkerVerificationJobResponse {
  jobId: string;
  total: number;
  dryRun: boolean;
}

export interface MarkerVerificationJobStatus {
  jobId: string;
  total: number;
  dryRun?: boolean;
  status: "running" | "paused" | "completed";
  summary: Partial<Record<StainMarkerVerificationStatus, number>>;
  results: StainMarkerVerificationResult[];
}

// Stain Providers service
export const stainProvidersService = {
  getAll: async () => {
    return api.get<StainProvider[]>("/admin/stain-providers");
  },

  getActive: async () => {
    return api.get<StainProvider[]>("/admin/stain-providers/active");
  },

  create: async (input: Partial<StainProvider>) => {
    return api.post<StainProvider>("/admin/stain-providers", input);
  },

  update: async (id: string, input: Partial<StainProvider>) => {
    return api.put<StainProvider>(`/admin/stain-providers/${id}`, input);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/stain-providers/${id}`);
  },
};
